/**
 * ReferralsScreen - Premium referrals screen matching v0 design
 */

import { useEffect, useState } from 'react';
import type { Agent } from '../types/agent';
import { getAgents, getReferralCode } from '../lib/api';
import { useTelegramUser, hapticFeedback } from '../lib/telegram';

interface ReferralsScreenProps {
  onAgentClick: (agentId: string) => void;
}

export function ReferralsScreen({ onAgentClick }: ReferralsScreenProps) {
  const [myAgents, setMyAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const user = useTelegramUser();

  useEffect(() => {
    async function fetchMyAgents() {
      try {
        const result = await getAgents({ limit: 100 });
        const owned = result.agents.filter(a => a.isOwner);
        setMyAgents(owned);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    fetchMyAgents();
  }, []);

  const copyReferralCode = async (agent: Agent) => {
    if (!agent.referral_code) {
      try {
        const res = await getReferralCode(agent.id);
        agent.referral_code = res.referral_code;
      } catch { return; }
    }
    const link = `https://t.me/tele_claw_bot/app?startapp=ref_${agent.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      hapticFeedback('light');
      setCopiedId(agent.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback
    }
  };

  // Calculate totals
  const totalReferrals = myAgents.reduce((sum, a) => sum + (a.referral_count || 0), 0);
  const totalUpvotes = myAgents.reduce((sum, a) => sum + (a.vote_count || 0), 0);

  if (loading) {
    return (
      <div className="space-y-4 pb-24 animate-screen-fade p-6">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-[#242424] animate-pulse mx-auto mb-3" />
          <div className="w-32 h-6 bg-[#242424] rounded animate-pulse mx-auto mb-2" />
          <div className="w-48 h-4 bg-[#242424] rounded animate-pulse mx-auto" />
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 rounded-xl bg-[#242424] animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto pb-24 animate-screen-fade">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-[#0098EA]/10 flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-[#0098EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Your Referrals</h1>
          <p className="text-[#a0a0a0]">Track your impact and rewards</p>
        </div>

        {/* Stats Card */}
        <div className="bg-gradient-to-br from-[#0098EA]/20 to-[#0098EA]/5 rounded-2xl p-6 border border-[#0098EA]/20">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-[#0098EA]/20 flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-[#0098EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{totalReferrals}</p>
              <p className="text-sm text-[#a0a0a0]">Total Referrals</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-[#0098EA]/20 flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-[#0098EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{totalUpvotes}</p>
              <p className="text-sm text-[#a0a0a0]">Total Upvotes</p>
            </div>
          </div>
        </div>

        {myAgents.length === 0 ? (
          <>
            {/* Copy Referral Link placeholder */}
            <button
              className="w-full bg-[#0098EA] hover:bg-[#0098EA]/90 text-white h-14 rounded-xl font-semibold text-base shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Referral Link
            </button>

            <div className="text-center py-8">
              <p className="text-4xl mb-4">🤖</p>
              <h3 className="text-base font-semibold text-white mb-2">No apps listed yet</h3>
              <p className="text-sm text-[#a0a0a0] mb-1">
                List your app on TeleClaw to start referring others!
              </p>
              {user && (
                <p className="text-xs text-[#a0a0a0]">
                  Signed in as {(user as any).first_name || (user as any).firstName || 'you'}
                </p>
              )}
            </div>

            {/* Info Card */}
            <div className="bg-[#242424]/50 border border-white/5 rounded-xl p-4">
              <p className="text-sm text-[#a0a0a0] text-center leading-relaxed">
                Share your referral link to earn rewards when users discover agents through your link
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Referred Apps List */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4">Your Referred Apps</h2>
              <div className="space-y-3">
                {myAgents.map(agent => (
                  <div
                    key={agent.id}
                    className="bg-[#242424] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => onAgentClick(agent.id)}
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#1a1a1a] flex items-center justify-center overflow-hidden flex-shrink-0">
                          {agent.logo ? (
                            <img src={agent.logo} alt={agent.name} className="w-full h-full object-cover" onError={(e) => {
                              const t = e.target as HTMLImageElement; t.style.display = 'none'; t.parentElement!.innerHTML = '<span class="text-2xl">🤖</span>';
                            }} />
                          ) : <span className="text-2xl">🤖</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white mb-1">{agent.name}</h3>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#0098EA]/10 text-[#0098EA] border border-[#0098EA]/20">
                            {agent.category}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center justify-around bg-[#2a2a2a]/30 rounded-lg p-3 mb-3">
                      <div className="text-center">
                        <p className="text-xl font-bold text-white mb-1">{agent.referral_count || 0}</p>
                        <p className="text-xs text-[#a0a0a0]">Referrals</p>
                      </div>
                      <div className="w-px h-8 bg-white/10" />
                      <div className="text-center">
                        <p className="text-xl font-bold text-white mb-1">{agent.vote_count || 0}</p>
                        <p className="text-xs text-[#a0a0a0]">Upvotes</p>
                      </div>
                    </div>

                    {/* Copy referral link */}
                    <button
                      onClick={() => copyReferralCode(agent)}
                      className={`
                        w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                        ${copiedId === agent.id
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-[#0098EA]/15 text-[#0098EA] active:bg-[#0098EA]/25'
                        }
                      `}
                    >
                      {copiedId === agent.id ? '✓ Link Copied!' : '📋 Copy Referral Link'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-[#242424]/50 border border-white/5 rounded-xl p-4">
              <p className="text-sm text-[#a0a0a0] text-center leading-relaxed">
                Share your referral link to earn rewards when users discover agents through your link
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
